import { Suspense, lazy, useState } from 'react';
import {
  createBrowserRouter,
  createMemoryRouter,
  RouterProvider,
  type RouteObject,
} from 'react-router';
import { DiagnosticsContext } from '../diagnostics/context';
import type { BrowserLogger } from '../diagnostics/browser-logger';
import { GuestProvider, type GuestServices } from '../features/guest/guest-state';
import { LearningProvider } from '../features/discovery-game/learning-state';
import { HubPage } from '../features/hub/HubPage';
import { AppErrorBoundary } from './AppErrorBoundary';
import { NotFound } from './NotFound';
import { Offered } from '../features/guest/offers';
import { Shell } from './Shell';

// Route-level code splitting: only the island entrance is in the initial bundle; the 3D engine
// itself is a separate chunk imported by the island (PROJECT_STATUS K4).
const WatchPage = lazy(() =>
  import('../features/catalogue/WatchPage').then((m) => ({ default: m.WatchPage })),
);
const CinemaRoom = lazy(() =>
  import('../features/cinema-room/CinemaRoom').then((m) => ({ default: m.CinemaRoom })),
);
const CinemaSearchPage = lazy(() =>
  import('../features/catalogue/WatchPage').then((m) => ({ default: m.CinemaSearchPage })),
);
const ProgrammePage = lazy(() =>
  import('../features/catalogue/ProgrammePage').then((m) => ({ default: m.ProgrammePage })),
);
const LabPage = lazy(() =>
  import('../features/discovery-game/LabPage').then((m) => ({ default: m.LabPage })),
);
const GamePage = lazy(() =>
  import('../features/discovery-game/GamePage').then((m) => ({ default: m.GamePage })),
);
const WorkshopPage = lazy(() =>
  import('../features/world-builder/WorkshopPage').then((m) => ({ default: m.WorkshopPage })),
);
const TowerPage = lazy(() =>
  import('../features/broadcaster-switch/TowerPage').then((m) => ({ default: m.TowerPage })),
);
const HelpPage = lazy(() => import('./InfoPages').then((m) => ({ default: m.HelpPage })));
const TreehousePage = lazy(() =>
  import('../features/treehouse/TreehousePage').then((m) => ({ default: m.TreehousePage })),
);
const StaffApp = lazy(() =>
  import('../features/editorial/StaffApp').then((m) => ({ default: m.StaffApp })),
);
const DiagnosticsPage = lazy(() =>
  import('../diagnostics/DiagnosticsPage').then((m) => ({ default: m.DiagnosticsPage })),
);

export const routes: RouteObject[] = [
  // The staff CMS has its own professional layout and is never linked from the children's shell.
  {
    path: '/staff/*',
    element: (
      <Suspense fallback={null}>
        <StaffApp />
      </Suspense>
    ),
  },
  {
    element: <Shell />,
    children: [
      { path: '/', element: <HubPage /> },
      { path: '/broadcaster', element: <TowerPage /> },
      // The Cinema is a room inside the world (ADR 0017); all films stay at /watch/all.
      { path: '/watch', element: <CinemaRoom /> },
      { path: '/watch/all', element: <WatchPage /> },
      { path: '/watch/search', element: <CinemaSearchPage /> },
      { path: '/programme/:id', element: <ProgrammePage /> },
      {
        path: '/play',
        element: (
          <Offered module="discovery-game" place="place.lab">
            <LabPage />
          </Offered>
        ),
      },
      {
        path: '/play/:missionId',
        element: (
          <Offered module="discovery-game" place="place.lab">
            <GamePage />
          </Offered>
        ),
      },
      {
        path: '/create',
        element: (
          <Offered module="world-builder" place="place.workshop">
            <WorkshopPage />
          </Offered>
        ),
      },
      { path: '/projects', element: <TreehousePage section="projects" /> },
      { path: '/skills', element: <TreehousePage section="skills" /> },
      { path: '/help', element: <HelpPage /> },
      // Hidden staff/tester route; intentionally absent from navigation.
      { path: '/diagnostics', element: <DiagnosticsPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];

const browserServices = (): GuestServices => ({
  idbFactory: typeof indexedDB === 'undefined' ? undefined : indexedDB,
  languages: typeof navigator === 'undefined' ? [] : navigator.languages,
});

export function App({
  logger,
  initialPath,
  services,
}: {
  logger: BrowserLogger;
  initialPath?: string;
  services?: GuestServices;
}) {
  const [router] = useState(() =>
    initialPath === undefined
      ? createBrowserRouter(routes)
      : createMemoryRouter(routes, { initialEntries: [initialPath] }),
  );
  const [guestServices] = useState(() => services ?? browserServices());
  return (
    <AppErrorBoundary logger={logger}>
      <DiagnosticsContext.Provider value={logger}>
        <GuestProvider services={guestServices}>
          <LearningProvider services={guestServices}>
            <RouterProvider router={router} />
          </LearningProvider>
        </GuestProvider>
      </DiagnosticsContext.Provider>
    </AppErrorBoundary>
  );
}
